// @ts-nocheck
import { getFocalPointStyles, parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getPageLinkByNodeId, LinkModel, useGetLinks } from "@insite/client-framework/Store/Links/LinksSelectors";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Icon from "@insite/mobius/Icon";
import ArrowRight from "@insite/mobius/Icons/ArrowRight";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { linkStyles } from "@store/Themes/common/mixins/link";
import breakpointValues from "@toolkit/Framework/Common/Utilities/breakpointValues";
import { pxToRem, pxToRemArray } from "@toolkit/Framework/Common/Utilities/unitHelper";
import parse from "html-react-parser";
import React, { FC } from "react";
import { css } from "styled-components";

const enum fields {
    background = "background",
    image = "image",
    links = "links",
    direction = "direction",
    focalPoint = "focalPoint",
    backgroundColor = "backgroundColor",
    heading = "heading",
    minimumHeight = "minimumHeight",
    bannerWidth = "bannerWidth",
    imageOverlay = "imageOverlay",
    partialOverlay = "partialOverlay",
    partialOverlayPositioning = "partialOverlayPositioning",
    contentPadding = "contentPadding",
    centerTextVertically = "centerTextVertically",
    responsiveFontSizes = "responsiveFontSizes",
    normalFontSize = "normalFontSize",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.background]: "image" | "color";
        [fields.image]: string;
        [fields.links]: LinkModel[];
        [fields.direction]: "vertical" | "horizontal";
        [fields.focalPoint]:
            | "topLeft"
            | "topCenter"
            | "topRight"
            | "centerLeft"
            | "center"
            | "centerRight"
            | "bottomLeft"
            | "bottomCenter"
            | "bottomRight";
        [fields.backgroundColor]: string;
        [fields.heading]: string;
        [fields.minimumHeight]: "1/4 viewport" | "1/2 viewport" | "3/4 viewport" | "fullViewport";
        [fields.imageOverlay]: string;
        [fields.partialOverlay]: boolean;
        [fields.partialOverlayPositioning]: "top" | "middle" | "bottom";
        [fields.contentPadding]: number;
        [fields.centerTextVertically]: boolean;
        [fields.responsiveFontSizes]: boolean;
        [fields.normalFontSize]: number;
    };
    extendedStyles?: BannerStyles;
}

export interface BannerStyles {
    wrapper?: InjectableCss;
    overlayWrapper?: GridContainerProps;
    slideCenteringWrapperStyles?: GridItemProps;
    linkWrapper?: GridItemProps;
    bannerLink?: LinkPresentationProps;
    bannerButton?: ButtonPresentationProps;
    list?: LinkPresentationProps;
    iconWrapper?: LinkPresentationProps;
    listWrapper?: InjectableCss;
    headingStyles?: InjectableCss;
}

export const bannerStyles: BannerStyles = {
    wrapper: {
        css: css`
            width: 100%;
            display: flex;
            margin-top: ${pxToRem(30)};
        `,
    },
    overlayWrapper: {
        css: css`
            width: 100%;
            color: white;

            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        padding: ${pxToRem(0, 50)} !important;
                    `,
                    css`
                        padding: ${pxToRem(50)} !important;
                    `,
                    css`
                        padding: ${pxToRem(50)} !important;
                    `,
                    css`
                        padding: ${pxToRem(100, 200)} !important;
                    `,
                    css`
                        padding: ${pxToRem(100, 200)} !important;
                    `,
                ]);
            }}
        `,
    },
    slideCenteringWrapperStyles: {
        width: [6, 6, 6, 6, 6],
        css: css`
            display: flex;
            flex-direction: row;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        justify-content: center;
                    `,
                    css`
                        justify-content: center;
                    `,
                    css`
                        justify-content: center;
                    `,
                    css`
                        justify-content: flex-start;
                    `,
                    css`
                        justify-content: flex-start;
                    `,
                ]);
            }}
        `,
    },
    headingStyles: {
        css: css`
            text-decoration: underline;
            text-decoration-color: ${getColor("primary")};
            text-align: left;
            text-underline-position: under;
            font-weight: bold;
            word-break: normal;
            ${breakpointValues(pxToRemArray([36, 48]), "font-size")}
        `,
    },
    linkWrapper: {
        width: [12, 12, 12, 6, 6],
        css: css`
            display: flex;
            flex-direction: column;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        padding: ${pxToRem(20, 50)};
                    `,
                    css`
                        padding: ${pxToRem(20, 25)};
                    `,
                    css`
                        padding: ${pxToRem(20, 50)};
                    `,
                    css`
                        padding: ${pxToRem(10, 50)};
                    `,
                    css`
                        padding: ${pxToRem(10, 50)};
                    `,
                ]);
            }}
        `,
    },
    list: {
        typographyProps: {
            transform: "uppercase",
            size: pxToRem(28),
            css: css`
                word-break: normal;
                ${({ theme }) => {
                    return breakpointMediaQueries(theme, [
                        css`
                            font-size: ${pxToRem(18)};
                        `,
                        css`
                            font-size: ${pxToRem(18)};
                        `,
                        css`
                            font-size: ${pxToRem(20)};
                        `,
                    ]);
                }}
            `,
        },
        css: css`
            ${linkStyles("tertiary.contrast", "primary", false, true)}
            &:hover {
                text-decoration: none;
            }
        `,
    },
    iconWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            justify-content: center;
            svg {
                color: ${getColor("primary.contrast")};
            }
        `,
    },
    listWrapper: {
        css: css`
            display: flex;
            width: 100%;
            flex-direction: row;
            align-items: flex-start;
            justify-content: space-between;
            border-bottom: ${pxToRem(1)} solid ${getColor("primary.contrast")};
            padding: ${pxToRem(20)} 0;
            &:last-child {
                border: none;
            }
        `,
    },
};

const HomepageBanner: FC<OwnProps> = ({ fields, extendedStyles }) => {
    const links = useGetLinks(fields.links, o => o.fields.destination);
    const backgroundStyles =
        fields.background === "image"
            ? `background-image: url(${fields.image});
           background-size: cover;`
            : `background-color: ${fields.backgroundColor};`;

    const focalPointStyles = getFocalPointStyles(fields.focalPoint);

    let minimumHeightStyles;
    switch (fields.minimumHeight) {
        case "1/4 viewport":
            minimumHeightStyles = "min-height: 25vh;";
            break;
        case "1/2 viewport":
            minimumHeightStyles = "min-height: 50vh;";
            break;
        case "3/4 viewport":
            minimumHeightStyles = "min-height: 75vh;";
            break;
        case "fullViewport":
            minimumHeightStyles = "min-height: 100vh;";
            break;
    }

    let overlayPositioningStyles;
    if (!fields.partialOverlay) {
        overlayPositioningStyles = "align-items: stretch;";
    } else if (fields.partialOverlayPositioning === "top") {
        overlayPositioningStyles = "align-items: flex-start;";
    } else if (fields.partialOverlayPositioning === "middle") {
        overlayPositioningStyles = "align-items: center;";
    } else if (fields.partialOverlayPositioning === "bottom") {
        overlayPositioningStyles = "align-items: flex-end;";
    }

    const [styles] = React.useState(() => mergeToNew(bannerStyles, extendedStyles));

    const wrapperStyles = {
        css: css`
            ${styles.wrapper?.css || ""}
            ${backgroundStyles}
            ${focalPointStyles}
            ${minimumHeightStyles}
            ${overlayPositioningStyles}
        `,
    };

    const overlayWrapperStyles = {
        css: css`
            ${styles.overlayWrapper?.css || ""}
            background-color: ${fields.background === "image" ? fields.imageOverlay : ""};
            padding: ${fields.contentPadding}px;
            ${fields.centerTextVertically &&
            `
                                    display: flex;
                                    flex-direction: row;
                                    align-items: center;
                                    justify-content:flex-start;`}
        `,
        offsetCss: css`
            justify-content: center;
        `,
    };

    return (
        <StyledWrapper {...wrapperStyles}>
            <GridContainer {...overlayWrapperStyles}>
                <GridItem {...styles.slideCenteringWrapperStyles}>
                    <Typography {...styles.headingStyles}>{parse(fields.heading, parserOptions)}</Typography>
                </GridItem>
                <GridItem {...styles.linkWrapper}>
                    {links.map((link, index) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <StyledWrapper key={index} {...styles.listWrapper}>
                            {link.url && (
                                <Link
                                    {...styles.list}
                                    href={link.url}
                                    target={fields.links[index].fields.openInNewWindow ? "_blank" : ""}
                                >
                                    {fields.links[index].fields.overriddenTitle || link.title}
                                </Link>
                            )}
                            <Link href={link.url} {...styles.iconWrapper}>
                                <Icon src={ArrowRight} />
                            </Link>
                        </StyledWrapper>
                    ))}
                </GridItem>
            </GridContainer>
        </StyledWrapper>
    );
};

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const settingsTab = {
    displayName: "Settings",
    sortOrder: 1,
};

const homepageBanner: WidgetModule = {
    component: HomepageBanner,
    definition: {
        group: "Basic",
        icon: "image-landscape",
        fieldDefinitions: [
            {
                fieldType: "General",
                name: fields.background,
                displayName: "Background",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Image", value: "image" },
                    { displayName: "Color", value: "color" },
                ],
                defaultValue: "image",
                hideEmptyOption: true,
                tab: basicTab,
            },
            {
                fieldType: "Translatable",
                name: fields.image,
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                isVisible: widget => widget.fields.background === "image",
                tab: basicTab,
            },
            {
                fieldType: "General",
                name: fields.imageOverlay,
                displayName: "Image Color Overlay",
                editorTemplate: "ColorPickerField",
                defaultValue: "",
                isVisible: widget => widget.fields.background === "image",
                tab: basicTab,
            },
            {
                fieldType: "General",
                name: fields.partialOverlay,
                editorTemplate: "CheckboxField",
                defaultValue: false,
                tab: basicTab,
            },
            {
                fieldType: "General",
                name: fields.partialOverlayPositioning,
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Top", value: "top" },
                    { displayName: "Middle", value: "middle" },
                    { displayName: "Bottom", value: "bottom" },
                ],
                hideEmptyOption: true,
                defaultValue: "bottom",
                isVisible: widget => widget.fields.partialOverlay,
                tab: basicTab,
            },
            {
                fieldType: "General",
                name: fields.focalPoint,
                displayName: "Focal Point",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Top Left", value: "topLeft" },
                    { displayName: "Top Center", value: "topCenter" },
                    { displayName: "Top Right", value: "topRight" },
                    { displayName: "Center Left", value: "centerLeft" },
                    { displayName: "Center", value: "center" },
                    { displayName: "Center Right", value: "centerRight" },
                    { displayName: "Bottom Left", value: "bottomLeft" },
                    { displayName: "Bottom Center", value: "bottomCenter" },
                    { displayName: "Bottom Right", value: "bottomRight" },
                ],
                defaultValue: "center",
                hideEmptyOption: true,
                isVisible: widget => widget.fields.background === "image",
                tab: basicTab,
            },
            {
                fieldType: "General",
                name: fields.backgroundColor,
                editorTemplate: "ColorPickerField",
                displayName: "Color",
                defaultValue: "black",
                isVisible: widget => widget.fields.background === "color",
                tab: basicTab,
            },
            {
                fieldType: "Translatable",
                name: fields.heading,
                editorTemplate: "RichTextField",
                displayName: "Heading",
                defaultValue: "",
                extendedConfig: { height: 100 },
                expandedToolbarButtons: {
                    moreMisc: {},
                    code: {},
                },
                tab: basicTab,
            },
            {
                fieldType: "General",
                name: fields.minimumHeight,
                displayName: "Minimum Banner Height",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "1/4 Viewport", value: "1/4 viewport" },
                    { displayName: "1/2 Viewport", value: "1/2 viewport" },
                    { displayName: "3/4 Viewport", value: "3/4 viewport" },
                    { displayName: "Full Viewport", value: "fullViewport" },
                ],
                defaultValue: "1/4 viewport",
                hideEmptyOption: true,
                tab: basicTab,
            },
            {
                name: fields.responsiveFontSizes,
                editorTemplate: "CheckboxField",
                fieldType: "General",
                tab: settingsTab,
                defaultValue: true,
            },
            {
                fieldType: "General",
                name: fields.contentPadding,
                editorTemplate: "IntegerField",
                defaultValue: 50,
                tab: basicTab,
            },
            {
                fieldType: "General",
                name: "centerTextVertically",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                tab: basicTab,
            },
            {
                name: fields.links,
                editorTemplate: "ListField",
                tab: basicTab,
                getDisplay: (item: HasFields, state) => {
                    const { type, value } = item.fields.destination;
                    if (type === "Page") {
                        const link = getPageLinkByNodeId(state, value);
                        return link?.title;
                    }
                    if (type === "Category") {
                        return value; // TODO ISC-10781 make this work
                    }
                    if (type === "Url") {
                        return value;
                    }
                    return value;
                },
                defaultValue: [],
                fieldType: "General",
                fieldDefinitions: [
                    {
                        name: "destination",
                        editorTemplate: "LinkField",
                        defaultValue: {
                            value: "",
                            type: "Page",
                        },
                        isRequired: true,
                    },
                    {
                        name: "overriddenTitle",
                        displayName: "Title",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: "openInNewWindow",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                ],
            },
            {
                name: fields.direction,
                editorTemplate: "DropDownField",
                defaultValue: "vertical",
                fieldType: "General",
                tab: basicTab,
                options: [
                    {
                        displayName: "Vertical",
                        value: "vertical",
                    },
                    {
                        displayName: "Horizontal",
                        value: "horizontal",
                    },
                ],
                hideEmptyOption: true,
            },
        ],
    },
};

export default homepageBanner;
